// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

import type { LatLon } from './tileMath';
import type { Wind } from './wind';

/**
 * Where the wind reading comes from.
 *
 * Open-Meteo needs no key and allows browser requests, which is what makes a client-side
 * call possible at all. Overridable so a deployment can point at its own proxy — moving
 * this behind the BFF is a URL change and nothing else.
 */
const DEFAULT_WEATHER_URL = 'https://api.open-meteo.com/v1/forecast';

const getWeatherUrl = (): string => {
  const configured = process.env.EXPO_PUBLIC_WEATHER_API_URL;
  return typeof configured === 'string' && configured.trim().length > 0
    ? configured.trim()
    : DEFAULT_WEATHER_URL;
};

/** Readings older than this are refetched; wind does not change meaningfully faster. */
const CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Coordinates are rounded to about a kilometre before they leave the device.
 *
 * Two reasons, and both matter: the whole course shares one reading, so eighteen holes make
 * one request instead of eighteen; and the request carries the course's rough position
 * rather than anything that could locate a person. The player's own fix is never sent.
 */
const COORD_PRECISION = 2;

type CacheEntry = { wind: Wind; fetchedAt: number };

const cache = new Map<string, CacheEntry>();

/** Test seam: clears the module-level cache between cases. */
export const __clearWindCache = () => cache.clear();

const cacheKey = (at: LatLon) =>
  `${at.lat.toFixed(COORD_PRECISION)},${at.lon.toFixed(COORD_PRECISION)}`;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export type HoleWindState = {
  wind: Wind | null;
  loading: boolean;
};

export type UseHoleWindParams = {
  /** Nothing is fetched until the view is open. */
  enabled: boolean;
  /** Where to report the wind for — the hole, never the player. */
  at: LatLon | null;
};

/**
 * Current wind at the hole.
 *
 * Failures are silent by design: a missing wind reading is not something the player can act
 * on, and an error banner over a rangefinder would be worse than no wind at all. The badge
 * simply does not appear.
 */
export const useHoleWind = ({ enabled, at }: UseHoleWindParams): HoleWindState => {
  const key = enabled && at ? cacheKey(at) : null;
  const [state, setState] = useState<HoleWindState>({ wind: null, loading: false });

  useEffect(() => {
    if (!key || !at) {
      setState({ wind: null, loading: false });
      return undefined;
    }

    const cached = cache.get(key);
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setState({ wind: cached.wind, loading: false });
      return undefined;
    }

    let cancelled = false;
    setState({ wind: cached?.wind ?? null, loading: true });

    const load = async () => {
      try {
        const [lat, lon] = key.split(',');
        const url =
          `${getWeatherUrl()}?latitude=${lat}&longitude=${lon}` +
          '&current=wind_speed_10m,wind_direction_10m,wind_gusts_10m&wind_speed_unit=ms';
        const response = await fetch(url);
        if (cancelled || !response.ok) return;

        const body = (await response.json()) as { current?: Record<string, unknown> };
        const speedMps = num(body.current?.wind_speed_10m);
        const fromDegrees = num(body.current?.wind_direction_10m);
        if (cancelled || speedMps === null || fromDegrees === null) return;

        const wind: Wind = { speedMps, fromDegrees, gustMps: num(body.current?.wind_gusts_10m) };
        cache.set(key, { wind, fetchedAt: Date.now() });
        setState({ wind, loading: false });
      } catch {
        // Silent: see the note above.
      } finally {
        if (!cancelled) setState((current) => ({ ...current, loading: false }));
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // `at` is only read through `key`, which is the rounded form: a metre of GPS drift must
    // not refire the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
};
